/**
 * Final Industry Templates (Part 3)
 * Manufacturing, Construction, Hospitality, Agriculture, Finance, Other
 */

import { IndustryTemplate } from '../types/industryTemplates';
import { SectionId } from '../types/businessPlan';

const createTemplate = (
  id: string, name: string, category: any, description: string, icon: string, popularity: number,
  keyMetrics: string[], revenueStreams: any[], expenses: any[], margins: any, startupCosts: any
): IndustryTemplate => ({
  id, name, category, description, icon, popularity, isActive: true, sectionTemplates: {},
  smartPrompts: { industryQuestions: [], editingHints: {} as Partial<Record<SectionId, string[]>> },
  financialDefaults: { typicalRevenueStreams: revenueStreams, typicalExpenses: expenses, benchmarks: margins, typicalStartupCosts: startupCosts },
  keyMetrics, commonChallenges: []
});

export const FINAL_TEMPLATES: IndustryTemplate[] = [
  // MANUFACTURING (5 templates)
  createTemplate('manufacturing', 'Manufacturing', 'manufacturing', 'Physical product manufacturing', '🏭', 70,
    ['Unit Cost', 'Production Capacity', 'Yield Rate', 'OEE', 'Inventory Turnover'],
    [{ name: 'Product Sales', category: 'product', avgUnitPrice: 50, avgUnitsPerMonth: 5000, growthRate: 15 }],
    [{ name: 'Materials', category: 'variable', avgAmount: 0, percentage: 45 }, { name: 'Labor', category: 'fixed', avgAmount: 25000 }],
    { grossMargin: { min: 35, avg: 45, max: 55 }, netMargin: { min: 8, avg: 15, max: 25 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 18, avg: 24, max: 36 }},
    { low: 200000, medium: 750000, high: 2500000 }
  ),
  
  createTemplate('3d-printing', '3D Printing Service', 'manufacturing', 'Custom 3D printing and prototyping', '🖨️', 72,
    ['Print Jobs', 'Material Usage', 'Turnaround Time', 'Print Success Rate'],
    [{ name: 'Printing Services', category: 'service', avgUnitPrice: 150, avgUnitsPerMonth: 200, growthRate: 20 }],
    [{ name: 'Materials', category: 'variable', avgAmount: 0, percentage: 30 }, { name: 'Equipment', category: 'fixed', avgAmount: 3000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 20, avg: 32, max: 45 }, revenueGrowth: { min: 18, avg: 30, max: 50 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 50000, medium: 150000, high: 400000 }
  ),
  
  createTemplate('electronics', 'Electronics Manufacturing', 'manufacturing', 'Electronic device assembly and production', '🔌', 68,
    ['Units Produced', 'Defect Rate', 'Assembly Time', 'Component Cost'],
    [{ name: 'Device Sales', category: 'product', avgUnitPrice: 200, avgUnitsPerMonth: 1000, growthRate: 18 }],
    [{ name: 'Components', category: 'variable', avgAmount: 0, percentage: 50 }, { name: 'Assembly', category: 'fixed', avgAmount: 20000 }],
    { grossMargin: { min: 35, avg: 45, max: 55 }, netMargin: { min: 10, avg: 18, max: 28 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 18, avg: 24, max: 36 }},
    { low: 300000, medium: 1000000, high: 3000000 }
  ),
  
  createTemplate('apparel-manufacturing', 'Apparel Manufacturing', 'manufacturing', 'Clothing design and production', '👗', 67,
    ['Units Produced', 'Fabric Cost', 'Order Lead Time', 'Return Rate'],
    [{ name: 'Wholesale Orders', category: 'product', avgUnitPrice: 25, avgUnitsPerMonth: 3000, growthRate: 15 }],
    [{ name: 'Materials', category: 'variable', avgAmount: 0, percentage: 40 }, { name: 'Production', category: 'variable', avgAmount: 0, percentage: 20 }],
    { grossMargin: { min: 38, avg: 45, max: 52 }, netMargin: { min: 8, avg: 15, max: 22 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 100000, medium: 400000, high: 1200000 }
  ),
  
  createTemplate('cosmetics', 'Cosmetics Manufacturing', 'manufacturing', 'Beauty and skincare product manufacturing', '💄', 74,
    ['Product Lines', 'Retail Accounts', 'DTC Revenue', 'Inventory Turnover'],
    [{ name: 'Product Sales', category: 'product', avgUnitPrice: 35, avgUnitsPerMonth: 2000, growthRate: 22 }],
    [{ name: 'Manufacturing', category: 'variable', avgAmount: 0, percentage: 30 }, { name: 'Marketing', category: 'fixed', avgAmount: 8000 }],
    { grossMargin: { min: 60, avg: 70, max: 80 }, netMargin: { min: 15, avg: 25, max: 38 }, revenueGrowth: { min: 18, avg: 30, max: 50 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 150000, medium: 500000, high: 1500000 }
  ),
  
  // CONSTRUCTION (4 templates)
  createTemplate('general-contractor', 'General Contractor', 'construction', 'Construction and renovation services', '🏗️', 68,
    ['Project Value', 'Gross Profit Margin', 'Project Completion', 'Safety Record'],
    [{ name: 'Project Fees', category: 'service', avgUnitPrice: 50000, avgUnitsPerMonth: 3, growthRate: 15 }],
    [{ name: 'Labor', category: 'variable', avgAmount: 0, percentage: 40 }, { name: 'Materials', category: 'variable', avgAmount: 0, percentage: 35 }],
    { grossMargin: { min: 18, avg: 25, max: 35 }, netMargin: { min: 5, avg: 12, max: 20 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 100000, medium: 300000, high: 800000 }
  ),
  
  createTemplate('landscaping', 'Landscaping', 'construction', 'Landscape design and maintenance', '🌳', 70,
    ['Recurring Clients', 'Project Revenue', 'Crew Productivity', 'Client Retention'],
    [{ name: 'Maintenance Contracts', category: 'subscription', avgUnitPrice: 300, avgUnitsPerMonth: 80, growthRate: 15 }],
    [{ name: 'Labor', category: 'fixed', avgAmount: 15000 }, { name: 'Equipment', category: 'fixed', avgAmount: 2500 }],
    { grossMargin: { min: 45, avg: 55, max: 65 }, netMargin: { min: 15, avg: 25, max: 35 }, revenueGrowth: { min: 12, avg: 20, max: 32 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 40000, medium: 120000, high: 300000 }
  ),
  
  createTemplate('plumbing', 'Plumbing Services', 'construction', 'Residential and commercial plumbing', '🔧', 69,
    ['Service Calls', 'Average Ticket', 'Response Time', 'Customer Satisfaction'],
    [{ name: 'Service Calls', category: 'service', avgUnitPrice: 250, avgUnitsPerMonth: 150, growthRate: 12 }],
    [{ name: 'Technician Wages', category: 'fixed', avgAmount: 18000 }, { name: 'Vehicle', category: 'fixed', avgAmount: 1500 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 18, avg: 28, max: 38 }, revenueGrowth: { min: 10, avg: 18, max: 28 }, breakEvenMonths: { min: 8, avg: 12, max: 18 }},
    { low: 50000, medium: 150000, high: 350000 }
  ),
  
  createTemplate('hvac', 'HVAC Services', 'construction', 'Heating, ventilation, and air conditioning', '❄️', 71,
    ['Installations', 'Service Contracts', 'Emergency Calls', 'Technician Utilization'],
    [{ name: 'Installations', category: 'service', avgUnitPrice: 5000, avgUnitsPerMonth: 15, growthRate: 15 }],
    [{ name: 'Technicians', category: 'fixed', avgAmount: 22000 }, { name: 'Parts', category: 'variable', avgAmount: 8000 }],
    { grossMargin: { min: 45, avg: 55, max: 65 }, netMargin: { min: 15, avg: 25, max: 35 }, revenueGrowth: { min: 12, avg: 20, max: 30 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 75000, medium: 200000, high: 500000 }
  ),
  
  // HOSPITALITY (4 templates)
  createTemplate('hotel', 'Hotel / Motel', 'hospitality', 'Lodging and accommodation services', '🏨', 66,
    ['Occupancy Rate', 'ADR', 'RevPAR', 'Guest Satisfaction'],
    [{ name: 'Room Revenue', category: 'service', avgUnitPrice: 120, avgUnitsPerMonth: 1500, growthRate: 10 }],
    [{ name: 'Staff', category: 'fixed', avgAmount: 30000 }, { name: 'Utilities', category: 'fixed', avgAmount: 8000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 10, avg: 20, max: 30 }, revenueGrowth: { min: 8, avg: 15, max: 25 }, breakEvenMonths: { min: 18, avg: 30, max: 48 }},
    { low: 500000, medium: 2000000, high: 10000000 }
  ),
  
  createTemplate('event-venue', 'Event Venue', 'hospitality', 'Event space rental and catering', '🎪', 68,
    ['Bookings', 'Event Revenue', 'Utilization Rate', 'Client Reviews'],
    [{ name: 'Event Bookings', category: 'service', avgUnitPrice: 3000, avgUnitsPerMonth: 15, growthRate: 15 }],
    [{ name: 'Venue Staff', category: 'variable', avgAmount: 8000 }, { name: 'Maintenance', category: 'fixed', avgAmount: 4000 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 15, avg: 25, max: 38 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 100000, medium: 300000, high: 800000 }
  ),
  
  createTemplate('tour-operator', 'Tour Operator', 'hospitality', 'Travel tours and experiences', '🗺️', 67,
    ['Tour Bookings', 'Average Group Size', 'Customer Satisfaction', 'Repeat Rate'],
    [{ name: 'Tour Sales', category: 'service', avgUnitPrice: 150, avgUnitsPerMonth: 300, growthRate: 18 }],
    [{ name: 'Guide Costs', category: 'variable', avgAmount: 0, percentage: 25 }, { name: 'Marketing', category: 'fixed', avgAmount: 3000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 18, avg: 30, max: 42 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 40000, medium: 120000, high: 300000 }
  ),
  
  createTemplate('cleaning-service', 'Cleaning Service', 'hospitality', 'Commercial or residential cleaning', '🧹', 72,
    ['Recurring Clients', 'Jobs per Week', 'Client Retention', 'Crew Efficiency'],
    [{ name: 'Cleaning Contracts', category: 'subscription', avgUnitPrice: 200, avgUnitsPerMonth: 150, growthRate: 18 }],
    [{ name: 'Staff Wages', category: 'fixed', avgAmount: 12000 }, { name: 'Supplies', category: 'variable', avgAmount: 1500 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 20, avg: 32, max: 45 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 6, avg: 10, max: 15 }},
    { low: 20000, medium: 60000, high: 150000 }
  ),
  
  // FITNESS & WELLNESS (3 templates)
  createTemplate('gym', 'Gym / Fitness Center', 'healthcare', 'Fitness facility and training', '🏋️', 69,
    ['Member Count', 'Retention Rate', 'Revenue per Member', 'Class Attendance'],
    [{ name: 'Memberships', category: 'subscription', avgUnitPrice: 60, avgUnitsPerMonth: 300, growthRate: 15 }],
    [{ name: 'Rent', category: 'fixed', avgAmount: 8000 }, { name: 'Staff', category: 'fixed', avgAmount: 12000 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 12, avg: 22, max: 32 }, revenueGrowth: { min: 12, avg: 20, max: 32 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 150000, medium: 400000, high: 1000000 }
  ),
  
  createTemplate('yoga-studio', 'Yoga / Pilates Studio', 'healthcare', 'Yoga and wellness studio', '🧘', 71,
    ['Class Attendance', 'Membership Revenue', 'Instructor Retention', 'Client Satisfaction'],
    [{ name: 'Memberships', category: 'subscription', avgUnitPrice: 120, avgUnitsPerMonth: 150, growthRate: 15 }],
    [{ name: 'Rent', category: 'fixed', avgAmount: 5000 }, { name: 'Instructors', category: 'variable', avgAmount: 8000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 20, avg: 32, max: 45 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 50000, medium: 150000, high: 350000 }
  ),
  
  createTemplate('personal-training', 'Personal Training', 'healthcare', 'One-on-one fitness coaching', '💪', 73,
    ['Active Clients', 'Sessions per Week', 'Client Results', 'Retention Rate'],
    [{ name: 'Training Sessions', category: 'service', avgUnitPrice: 80, avgUnitsPerMonth: 200, growthRate: 18 }],
    [{ name: 'Facility Rent', category: 'fixed', avgAmount: 2000 }, { name: 'Insurance', category: 'fixed', avgAmount: 500 }],
    { grossMargin: { min: 70, avg: 80, max: 88 }, netMargin: { min: 35, avg: 50, max: 65 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 4, avg: 8, max: 12 }},
    { low: 10000, medium: 30000, high: 80000 }
  ),
  
  // AGRICULTURE (2 templates)
  createTemplate('organic-farm', 'Organic Farm', 'agriculture', 'Organic crop production and sales', '🌾', 64,
    ['Yield per Acre', 'Crop Diversity', 'Wholesale Accounts', 'CSA Members'],
    [{ name: 'Produce Sales', category: 'product', avgUnitPrice: 5000, avgUnitsPerMonth: 12, growthRate: 12 }],
    [{ name: 'Labor', category: 'fixed', avgAmount: 8000 }, { name: 'Supplies', category: 'variable', avgAmount: 2000 }],
    { grossMargin: { min: 40, avg: 50, max: 60 }, netMargin: { min: 10, avg: 20, max: 30 }, revenueGrowth: { min: 8, avg: 15, max: 25 }, breakEvenMonths: { min: 18, avg: 30, max: 48 }},
    { low: 100000, medium: 300000, high: 800000 }
  ),
  
  createTemplate('livestock', 'Livestock Farming', 'agriculture', 'Animal husbandry and production', '🐄', 62,
    ['Head Count', 'Feed Conversion', 'Market Weight', 'Death Loss %'],
    [{ name: 'Livestock Sales', category: 'product', avgUnitPrice: 800, avgUnitsPerMonth: 50, growthRate: 10 }],
    [{ name: 'Feed', category: 'variable', avgAmount: 0, percentage: 50 }, { name: 'Veterinary', category: 'variable', avgAmount: 2000 }],
    { grossMargin: { min: 35, avg: 45, max: 55 }, netMargin: { min: 8, avg: 15, max: 25 }, revenueGrowth: { min: 8, avg: 12, max: 20 }, breakEvenMonths: { min: 18, avg: 30, max: 48 }},
    { low: 200000, medium: 600000, high: 2000000 }
  ),
  
  // FINANCE (2 templates)
  createTemplate('financial-advisory', 'Financial Advisory', 'finance', 'Wealth management and financial planning', '💰', 76,
    ['AUM', 'Client Count', 'Net Flows', 'Client Retention'],
    [{ name: 'Advisory Fees', category: 'service', avgUnitPrice: 2000, avgUnitsPerMonth: 20, growthRate: 15 }],
    [{ name: 'Advisor Salaries', category: 'fixed', avgAmount: 18000 }, { name: 'Compliance', category: 'fixed', avgAmount: 2000 }],
    { grossMargin: { min: 60, avg: 70, max: 80 }, netMargin: { min: 25, avg: 40, max: 55 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 50000, medium: 150000, high: 400000 }
  ),
  
  createTemplate('insurance-agency', 'Insurance Agency', 'finance', 'Insurance sales and services', '🛡️', 68,
    ['Policies Written', 'Retention Rate', 'Commission Revenue', 'Cross-Sell Rate'],
    [{ name: 'Commissions', category: 'service', avgUnitPrice: 500, avgUnitsPerMonth: 50, growthRate: 12 }],
    [{ name: 'Agent Salaries', category: 'fixed', avgAmount: 15000 }, { name: 'Marketing', category: 'fixed', avgAmount: 2000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 20, avg: 32, max: 45 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 40000, medium: 120000, high: 300000 }
  )
];

export function getFinalTemplates(): IndustryTemplate[] {
  return FINAL_TEMPLATES;
}
